import { useCallback } from 'react';
import { useNotificationStore } from './store';
import MuiDialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import useTranslations from '@/hooks/useTranslations';
import dynamic from 'next/dynamic';

// The markdown stack (~100 KiB) is needed only once a notification opens, not on every first load
const Markdown = dynamic(() => import('@/components/Markdown'));

export default function Dialog() {
  const { t } = useTranslations();
  const { notification, handleClose } = useNotificationStore();

  const onCancel = useCallback(() => {
    handleClose();
    notification.onCancel?.();
  }, [handleClose, notification]);

  const onConfirm = useCallback(() => {
    handleClose();
    notification.onConfirm?.();
  }, [handleClose, notification]);

  if (notification.type !== 'dialog') return null;

  return (
    <MuiDialog
      open={notification.open ?? false}
      onClose={onCancel}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: notification.blur ? 'blur(5px)' : undefined,
          },
        },
      }}
      data-lenis-prevent
    >
      <DialogTitle id='alert-dialog-title' sx={{ position: 'relative' }}>
        {notification.title}

        <IconButton
          aria-label='close'
          onClick={onCancel}
          size='small'
          sx={{ position: 'absolute', right: 'var(--mui-spacing)' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {notification.message && (
          <Box id='alert-dialog-description' sx={{ color: 'text.secondary' }}>
            <Markdown markdown={notification.message} />
          </Box>
        )}
        {notification.content}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color='inherit' variant='text' size='medium'>
          {notification.cancelText || t('buttons.cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          autoFocus
          color={notification.severity}
          variant='text'
          size='medium'
        >
          {notification.confirmText || t('buttons.continue')}
        </Button>
      </DialogActions>
    </MuiDialog>
  );
}
